import { config } from 'config';
import isEmpty from 'lodash/isEmpty';
import { officeActions } from 'core/Office';
import { optionActions } from 'core/Option';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Checkbox,
    ExpandButton,
    FormGroup,
    Input,
    SelectSearch
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    ConfirmationModal,
    ErrorMessage,
    FormLayout,
    PhoneInput
} from 'views/components';
import React, { useEffect, useState } from 'react';
import {
    uppercaseFirst,
    validationSchema
} from 'shared/utils';
import {
    useDispatch,
    useErrors,
    useObjectState,
    useSelector
} from 'hooks';
import {
    useHistory,
    useParams
} from 'react-router-dom';
const { roomTimeAvailability } = config;

const DEFAULT_OFFICE = {
    address: '',
    available_time_ids: [],
    city_id: 0,
    deleted: false,
    district_id: 0,
    email: '',
    floor: '',
    house_number: '',
    name: '',
    office_type_id: 0,
    phone_number: '',
    postal_code: '',
    province_id: 0
};

const OfficeRegistration = () => {

    const dispatch = useDispatch();
    const { push } = useHistory();
    const { t } = useTranslation();
    const {
        id,
        mode
    } = useParams();
    const officeId = Number(id);
    const {
        loading,
        selected: selectedOffice
    } = useSelector('office');
    const {
        cities: allCities,
        provinces: allProvinces
    } = useSelector('option');

    const [form, setForm, setFormValue] = useObjectState(DEFAULT_OFFICE);
    const [modal, setModal] = useState('');
    const { clearError, errors, setErrors } = useErrors();

    useEffect(() => {
        dispatch(optionActions.getProvinces());
    }, []);

    useEffect(() => {
        if ([t('crear')].includes(mode) && !id) return;
        if ([t('editar'), t('ver')].includes(mode) && !!id) return;
        push(t('/consultorios/listar'));
    }, [mode]);

    useEffect(() => {
        if ([t('crear')].includes(mode)) {
            dispatch(officeActions.setOfficeSelected({}));
        }
        if ([t('editar'), t('ver')].includes(mode) && officeId) {
            dispatch(officeActions.getOffice(officeId));
        }
    }, [mode, officeId, setForm]);

    useEffect(() => {
        const {
            available_times = [],
            city,
            office_type,
            province
        } = (isEmpty(selectedOffice) ? DEFAULT_OFFICE : selectedOffice);
        setForm({
            ...selectedOffice,
            available_time_ids: available_times.map(({ id }) => id),
            city_id: city ? city.id : 0,
            office_type_id: office_type ? office_type.id : 0,
            province_id: province ? province.id : 0
        });
        if (province && province.id) {
            dispatch(optionActions.getCities(province.id));
        }
    }, [selectedOffice, setForm]);

    const handleAvailability = value => {
        clearError('available_time_ids');
        const item = form.available_time_ids.find(id => id === value);
        let newItems;
        if (!!item) {
            newItems = form.available_time_ids.filter(item => item !== value);
        } else {
            newItems = form.available_time_ids.concat(value);
        }
        setFormValue('available_time_ids', newItems);
    };

    const handleChange = id => value => {
        clearError(id);
        if (id === 'province_id') {
            clearError('city_id');
            setFormValue('city_id', null);
            dispatch(optionActions.getCities(value));
        }
        setFormValue(id, value);
    };

    const handleModalResponse = value => {
        if (value === 'confirm' && modal === 'delete') {
            dispatch(officeActions.officeDelete(form.id));
        }
        if (value === 'confirm' && modal === 'restore') {
            dispatch(officeActions.officeRestore(form.id));
        }
        setModal('');
    };

    const handleSubmit = () => {
        if (validate()) {
            const { city_id, province_id, ...payload } = form;
            dispatch(officeActions.officeRegistration({
                ...payload,
                ...(!!city_id && { city_id }),
                ...(!!province_id && { province_id })
            }));
        }
    };

    const physical = form.office_type_id === 1;

    const validate = () => {
        const { errors, isValid } = validationSchema({
            name: [!!form.name, t('Requerido')],
            office_type_id: [!!form.office_type_id, t('Requerido')],
            ...(!!physical && { address: [!!form.address, t('Requerido')] })
        });
        setErrors(errors);
        return isValid;
    };
    
    const confirmTitle = modal === 'delete' ? t('¿Quieres dar de baja este consultorio?') : t('¿Quieres restaurar este consultorio?');
    const editMode = mode === t('editar') && !form.deleted;
    const viewMode = mode === t('ver');

    return (
        <FormLayout
            goBackTo={ t('/consultorios/listar') }
            loading={ loading }
            title={ `${ uppercaseFirst(mode) } ${ t('consultorio') }` }
        >
            <Row>
                <Col>
                    <Row>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Tipo de consultorio') }>
                                <SelectSearch
                                    disabled={ viewMode || editMode }
                                    hasError={ !!errors.office_type_id }
                                    options={ [{ label: t('Físico'), value: 1 }, { label: t('Virtual'), value: 2 }] }
                                    placeholder={ t('Tipo') }
                                    value={ form.office_type_id }
                                    width="100%"
                                    onChange={ handleChange('office_type_id') }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Nombre') }>
                                <Input
                                    disabled={ viewMode }
                                    error={ errors.name }
                                    hasError={ !!errors.name }
                                    placeholder={ t('Escriba un nombre') }
                                    type="text"
                                    value={ form.name }
                                    onChange={ handleChange('name') }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {
                        physical &&
                        <Row>
                            <Col md="6" sm="12">
                                <FormGroup label={ t('Dirección') }>
                                    <Input
                                        disabled={ viewMode }
                                        error={ errors.address }
                                        hasError={ !!errors.address }
                                        placeholder={ t('Escriba una dirección') }
                                        type="text"
                                        value={ form.address }
                                        onChange={ handleChange('address') }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2" sm="4">
                                <FormGroup label="N°">
                                    <Input
                                        disabled={ viewMode }
                                        error={ errors.house_number }
                                        hasError={ !!errors.house_number }
                                        placeholder=""
                                        type="number"
                                        value={ form.house_number ?? '' }
                                        onChange={ handleChange('house_number') }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2" sm="4">
                                <FormGroup label={ t('Piso') }>
                                    <Input
                                        disabled={ viewMode }
                                        placeholder=""
                                        type="text"
                                        value={ form.floor ?? '' }
                                        onChange={ handleChange('floor') }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="2" sm="4">
                                <FormGroup label={ t('Código postal') }>
                                    <Input
                                        disabled={ viewMode }
                                        error={ errors.postal_code }
                                        hasError={ !!errors.postal_code }
                                        maxlength={ 8 }
                                        placeholder=""
                                        type="number"
                                        value={ form.postal_code ?? '' }
                                        onChange={ handleChange('postal_code') }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6" sm="12">
                                <FormGroup label={ t('Provincia') }>
                                    <SelectSearch
                                        disabled={ viewMode }
                                        hasError={ !!errors.province_id }
                                        margin="0px 0px 20px 0px"
                                        options={ allProvinces }
                                        placeholder={ t('Ingrese Provincia') }
                                        value={ form.province_id }
                                        width="100%"
                                        onChange={ handleChange('province_id') }
                                    />
                                </FormGroup>
                            </Col>
                            <Col md="6" sm="12">
                                <FormGroup label={ t('Ciudad') }>
                                    <SelectSearch
                                        disabled={ viewMode }
                                        hasError={ !!errors.city_id }
                                        margin="0px 0px 20px 0px"
                                        options={ allCities }
                                        placeholder={ t('Ingrese Ciudad') }
                                        value={ form.city_id }
                                        width="100%"
                                        onChange={ handleChange('city_id') }
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    <Row>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Mail de contacto') }>
                                <Input
                                    disabled={ viewMode }
                                    placeholder=""
                                    type="email"
                                    value={ form.email ?? '' }
                                    onChange={ handleChange('email') }
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6" sm="12">
                            <FormGroup label={ t('Teléfono') }>
                                <PhoneInput
                                    disabled={ viewMode }
                                    name="phone_number"
                                    value={ form.phone_number }
                                    onChange={ handleChange('phone_number') }
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    {
                        roomTimeAvailability &&
                        <Row>
                            <Col md="8" sm="8">
                                <FormGroup label={ t('Disponibilidad horaria') }>
                                    <Row>
                                        <Col md="4" sm="12">
                                            <Checkbox
                                                checked={ form.available_time_ids.includes(1) }
                                                disabled={ viewMode }
                                                label="06:00 - 14:00"
                                                onClick={ () => handleAvailability(1) }
                                            />
                                        </Col>
                                        <Col md="4" sm="12">
                                            <Checkbox
                                                checked={ form.available_time_ids.includes(2) }
                                                disabled={ viewMode }
                                                label="14:00 - 22:00"
                                                onClick={ () => handleAvailability(2) }
                                            />
                                        </Col>
                                        <Col md="4" sm="12">
                                            <Checkbox
                                                checked={ form.available_time_ids.includes(3) }
                                                disabled={ viewMode }
                                                label="22:00 - 06:00"
                                                onClick={ () => handleAvailability(3) }
                                            />
                                        </Col>
                                    </Row>
                                    <ErrorMessage error={ errors.available_time_ids } />
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    <ButtonFooter margin="20px 0px 0px 0px">
                        {
                            editMode && !form.deleted &&
                            <ExpandButton danger caption={ t('Eliminar') } size={ 100 } onClick={ () => setModal('delete') } />
                        }
                        {
                            form.deleted &&
                            <ExpandButton caption={ t('Restaurar') } size={ 100 } onClick={ () => setModal('restore') } />
                        }
                        {
                            ((!viewMode && !editMode) || editMode) && !form.deleted &&
                            <ExpandButton caption={ t('Confirmar') } size={ 100 } onClick={ handleSubmit } />
                        }
                    </ButtonFooter>
                </Col>
            </Row>
            <ConfirmationModal
                actionHandler={ handleModalResponse }
                active={ !!modal }
                title={ confirmTitle }
            />
        </FormLayout>
    );
};

export default OfficeRegistration;
